import * as React from 'react';
import {
  RecapPlayer,
  type RecapChapter,
  type RecapPlayerState,
} from '@hauraki/ui';
import api from '../../services/api';
import { getSocket } from '../../services/socket';

/**
 * MV5 Sprint 2.B — Smart wrapper around the `@hauraki/ui` RecapPlayer.
 *
 * Owns the end-to-end lifecycle:
 *   1. On mount (state="idle"), lazily fetches chapters when the learner
 *      expands to V2. Nothing is fetched for a chip-only render — we don't
 *      want to hammer session-manager for every AI-tutor suggestion the user
 *      never expands.
 *   2. On V2 → `onSelect`, POSTs /recaps/stitch, transitions to V5.
 *   3. Subscribes to `recap:progress` on socket.io. WS event with status
 *      `ready` transitions to V3 with the returned audio URL. `error`
 *      transitions to V4-error.
 *   4. On V3 `onEnd`, transitions to V4-happy.
 *   5. Idempotency: on reconnect the WS handler ACKs with the last cached
 *      event so a page refresh doesn't drop the user back to V5 forever.
 *
 * Wave 2.1+ items:
 *  - React-player abstraction so the V3 <audio> can swap to a video/canvas
 *    render when the pipeline produces a stitched video.
 *  - Optimistic pre-stitch when the AI tutor is confident enough — send
 *    the stitch request without user click, present V3 with a "peek"
 *    countdown.
 */

interface Props {
  lectureId: string;
  lectureTitle?: string;
  suggestedSegmentIds?: string[];
  /** Called when the user dismisses V1 (top-right ×). */
  onDismiss?: () => void;
  /** Called when the user hits "Ask a follow-up" in V4. */
  onFollowUp?: () => void;
  className?: string;
}

interface JobResponse {
  job_id: string;
  estimated_time_ms: number;
  status: 'extracting' | 'stitching' | 'ready' | 'error';
}

const RecapPlayerContainer: React.FC<Props> = ({
  lectureId,
  lectureTitle,
  suggestedSegmentIds,
  onFollowUp,
  className,
}) => {
  const [state, setState] = React.useState<RecapPlayerState>('idle');
  const [chapters, setChapters] = React.useState<RecapChapter[] | undefined>(
    undefined,
  );
  const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
  const [stitchProgressPct, setStitchProgressPct] = React.useState<number | null>(
    null,
  );
  const [stitchStage, setStitchStage] = React.useState<
    'extracting' | 'stitching' | null
  >(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const jobIdRef = React.useRef<string | null>(null);

  // Lazy-load chapters when transitioning to expanded (V2).
  const loadChapters = React.useCallback(async () => {
    try {
      const res = await api.get<{ chapters: RecapChapter[] }>(
        `/lectures/${lectureId}/chapters`,
      );
      setChapters(res.data.chapters ?? []);
    } catch {
      // Empty state renders "No chapters yet"; treat network failure the
      // same and let the parent surface a toast if it wants.
      setChapters([]);
    }
  }, [lectureId]);

  const handlePlay = React.useCallback(() => {
    // V1 Play button — jump straight to V2 with the suggested segments
    // pre-selected. If suggestedSegmentIds is present the user can hit
    // "Play recap" immediately.
    setState('expanded');
    void loadChapters();
  }, [loadChapters]);

  const handleExpand = React.useCallback(() => {
    setState('expanded');
    void loadChapters();
  }, [loadChapters]);

  const handleCollapse = React.useCallback(() => {
    setState('idle');
  }, []);

  const handleSelect = React.useCallback(
    async (segmentIds: string[]) => {
      setState('loading');
      setStitchProgressPct(0);
      setStitchStage('extracting');
      setErrorMessage(null);
      try {
        // segmentIds coming from V2 selection are chapter ids; the server
        // treats each chapter as a self-contained segment when segments
        // aren't authored yet. In Wave 2.1 the frontend will resolve
        // segment ids explicitly via a /lectures/:id/segments call.
        const res = await api.post<JobResponse>('/recaps/stitch', {
          lecture_id: lectureId,
          segment_ids: segmentIds,
        });
        jobIdRef.current = res.data.job_id;
      } catch (err) {
        setState('ended');
        setErrorMessage(
          (err as Error)?.message ??
            'Could not start the recap. Please try again.',
        );
      }
    },
    [lectureId],
  );

  const handleEnd = React.useCallback(() => {
    setState('ended');
  }, []);

  const handleReplay = React.useCallback(() => {
    if (audioUrl) setState('playing');
    else setState('expanded');
  }, [audioUrl]);

  const handleFollowUp = React.useCallback(() => {
    onFollowUp?.();
  }, [onFollowUp]);

  // WS wiring: subscribe on job creation, unsubscribe on unmount / job
  // change.
  React.useEffect(() => {
    if (state !== 'loading') return;
    const jobId = jobIdRef.current;
    if (!jobId) return;
    const socket = getSocket();
    const onProgress = (evt: {
      jobId?: string;
      status?: 'extracting' | 'stitching' | 'ready' | 'error';
      progress_pct?: number;
      result_audio_url?: string | null;
      error_message?: string | null;
    }) => {
      if (evt.jobId && evt.jobId !== jobId) return;
      if (typeof evt.progress_pct === 'number') {
        setStitchProgressPct(evt.progress_pct);
      }
      if (evt.status === 'extracting' || evt.status === 'stitching') {
        setStitchStage(evt.status);
      }
      if (evt.status === 'ready' && evt.result_audio_url) {
        setAudioUrl(evt.result_audio_url);
        setState('playing');
      } else if (evt.status === 'error') {
        setErrorMessage(evt.error_message ?? 'Recap stitching failed.');
        setState('ended');
      }
    };
    socket.emit(
      'recap:subscribe',
      { jobId },
      (ack: { ok?: boolean; last?: Parameters<typeof onProgress>[0] }) => {
        if (ack?.last) onProgress(ack.last);
      },
    );
    socket.on('recap:progress', onProgress);
    return () => {
      socket.emit('recap:unsubscribe', { jobId });
      socket.off('recap:progress', onProgress);
    };
  }, [state]);

  return (
    <RecapPlayer
      lectureId={lectureId}
      lectureTitle={lectureTitle}
      state={state}
      chapters={chapters}
      selectedSegmentIds={suggestedSegmentIds}
      audioUrl={audioUrl}
      stitchProgressPct={stitchProgressPct}
      stitchStage={stitchStage}
      errorMessage={errorMessage}
      onPlay={handlePlay}
      onExpand={handleExpand}
      onCollapse={handleCollapse}
      onSelect={handleSelect}
      onEnd={handleEnd}
      onReplay={handleReplay}
      onFollowUp={handleFollowUp}
      className={className}
    />
  );
};

export default RecapPlayerContainer;
